/**
 * Care Team Center — Client-Caregiver Relationships
 * 
 * The operational hub for managing ongoing relationships between clients and caregivers.
 * 
 * Assignment Hierarchy:
 * - Primary Caregiver: Normally provides care, scheduled first
 * - Secondary Caregiver: Regularly works with client, preferred replacement  
 * - Backup Caregiver(s): Qualified for coverage when needed
 * 
 * Two views:
 * 1. Client View: Select client → See/manage care team
 * 2. Caregiver View: Select caregiver → See assigned clients and workload
 * 
 * Philosophy: "Relationships should be a first-class platform object."
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Users, Heart, UserPlus, Search, ChevronRight, 
  AlertTriangle, CheckCircle, Clock, MapPin, Shield,
  TrendingUp, AlertCircle, Briefcase, Phone, Calendar,
  X, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api from "../../lib/api";

// Tier badge styles
const TIER_STYLES = {
  primary: "bg-green-100 text-green-800 border-green-200",
  secondary: "bg-blue-100 text-blue-800 border-blue-200",
  backup: "bg-gray-100 text-gray-700 border-gray-200",
};

const TIER_LABELS = {
  primary: "Primary",
  secondary: "Secondary",
  backup: "Backup",
};

export default function CareTeamCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("view") || "clients");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Client view state
  const [clients, setClients] = useState({ missing_primary: [], missing_backup: [], fully_assigned: [], summary: {} });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientCareTeam, setClientCareTeam] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  
  // Caregiver view state
  const [caregivers, setCaregivers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [caregiverAssignments, setCaregiverAssignments] = useState(null);
  const [caregiverSearch, setCaregiverSearch] = useState("");
  
  // Assignment dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [availableCaregivers, setAvailableCaregivers] = useState([]);
  const [assignForm, setAssignForm] = useState({
    caregiver_id: "",
    tier: "primary",
    notes: "",
  });
  const [validationWarnings, setValidationWarnings] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);

  // Fetch dashboard metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const res = await api.get("/client-assignments/dashboard");
      setMetrics(res.data);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
    }
  }, []);

  // Fetch clients needing assignment
  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get("/client-assignments/clients-needing-assignment");
      setClients(res.data);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  }, []);

  // Fetch available caregivers
  const fetchCaregivers = useCallback(async () => {
    try {
      const res = await api.get("/client-assignments/available-caregivers");
      setCaregivers(res.data.caregivers || []);
    } catch (err) {
      console.error("Failed to fetch caregivers:", err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMetrics(), fetchClients(), fetchCaregivers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchMetrics, fetchClients, fetchCaregivers]);

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ view: activeTab });
  }, [activeTab, setSearchParams]);

  // Fetch care team for selected client
  const fetchClientCareTeam = async (clientId) => {
    try {
      const res = await api.get(`/client-assignments/client/${clientId}/care-team`);
      setClientCareTeam(res.data);
    } catch (err) {
      console.error("Failed to fetch care team:", err);
      toast.error("Failed to load care team");
    }
  };

  // Fetch assignments for selected caregiver
  const fetchCaregiverAssignments = async (caregiverId) => {
    try {
      const res = await api.get(`/client-assignments/caregiver/${caregiverId}/assignments`);
      setCaregiverAssignments(res.data);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      toast.error("Failed to load caregiver assignments");
    }
  };

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientCareTeam(null);
    fetchClientCareTeam(client.id);
  };

  // Handle caregiver selection
  const handleSelectCaregiver = (caregiver) => {
    setSelectedCaregiver(caregiver);
    setCaregiverAssignments(null);
    fetchCaregiverAssignments(caregiver.id);
  };

  // Open assign dialog for client
  const handleAssignToClient = async (client) => {
    setAssignTarget(client);
    setAssignForm({
      caregiver_id: "",
      tier: client.has_primary ? (client.has_secondary ? "backup" : "secondary") : "primary",
      notes: "",
    });
    setValidationWarnings(null);
    
    // Fetch available caregivers
    try {
      const res = await api.get("/client-assignments/available-caregivers", {
        params: { client_id: client.id }
      });
      setAvailableCaregivers(res.data.caregivers || []);
    } catch (err) {
      console.error(err);
    }
    
    setShowAssignDialog(true);
  };

  // Submit assignment
  const handleSubmitAssignment = async (override = false) => {
    if (!assignForm.caregiver_id || !assignTarget) {
      toast.error("Please select a caregiver");
      return;
    }
    
    setAssignLoading(true);
    try {
      const res = await api.post(`/client-assignments/client/${assignTarget.id}/care-team`, {
        caregiver_id: assignForm.caregiver_id,
        tier: assignForm.tier,
        notes: assignForm.notes,
        override_warnings: override,
        override_reason: override ? "Administrator override" : null,
      });
      
      if (res.data.status === "validation_warnings") {
        setValidationWarnings(res.data.warnings);
        return;
      }
      
      toast.success(res.data.message || "Assignment created successfully");
      setShowAssignDialog(false);
      setValidationWarnings(null);
      
      // Refresh data
      await Promise.all([fetchMetrics(), fetchClients(), fetchCaregivers()]);
      
      if (selectedClient) {
        fetchClientCareTeam(selectedClient.id);
      }
      if (selectedCaregiver) {
        fetchCaregiverAssignments(selectedCaregiver.id);
      }
    } catch (err) {
      console.error("Assignment error:", err);
      if (err.response?.data?.detail?.warnings) {
        setValidationWarnings(err.response.data.detail.warnings);
      } else {
        toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to create assignment");
      }
    } finally {
      setAssignLoading(false);
    }
  };

  // End relationship
  const handleEndRelationship = async (relationshipId, reason) => {
    try {
      await api.post(`/client-assignments/relationship/${relationshipId}/end`, {
        reason: reason || "Assignment ended",
      });
      toast.success("Assignment ended");
      
      // Refresh data
      if (selectedClient) {
        fetchClientCareTeam(selectedClient.id);
      }
      if (selectedCaregiver) {
        fetchCaregiverAssignments(selectedCaregiver.id);
      }
      await Promise.all([fetchMetrics(), fetchClients()]);
    } catch (err) {
      console.error("End relationship error:", err);
      toast.error("Failed to end assignment");
    }
  };

  // Filter functions
  const filteredMissingPrimary = (clients.missing_primary || []).filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );
  const filteredMissingBackup = (clients.missing_backup || []).filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );
  const filteredFullyAssigned = (clients.fully_assigned || []).filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredCaregivers = caregivers.filter(cg =>
    cg.name?.toLowerCase().includes(caregiverSearch.toLowerCase()) ||
    cg.email?.toLowerCase().includes(caregiverSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="care-team-center">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Care Team Center</h1>
          <p className="text-muted-foreground">
            Manage client-caregiver relationships and care team assignments
          </p>
        </div>
      </div>

      {/* Dashboard Metrics - Friendly Operational Language */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Client Metrics */}
          <MetricCard
            label="Active Clients"
            description="Clients receiving care services"
            value={metrics.client_metrics.total_clients}
            icon={Heart}
            color="blue"
          />
          <MetricCard
            label="Assigned Care Teams"
            description="Clients with caregivers assigned"
            value={metrics.client_metrics.clients_assigned}
            icon={CheckCircle}
            color="green"
          />
          <MetricCard
            label="Needs Primary Caregiver"
            description="Clients without a designated primary caregiver"
            value={metrics.client_metrics.clients_missing_primary}
            icon={AlertTriangle}
            color={metrics.client_metrics.clients_missing_primary > 0 ? "red" : "gray"}
            urgent={metrics.client_metrics.clients_missing_primary > 0}
          />
          <MetricCard
            label="Needs Backup Caregiver"
            description="Clients without backup coverage"
            value={metrics.client_metrics.clients_missing_backup}
            icon={AlertCircle}
            color={metrics.client_metrics.clients_missing_backup > 0 ? "amber" : "gray"}
          />

          {/* Caregiver Metrics */}
          <MetricCard
            label="Active Caregivers"
            description="Caregivers available for assignments"
            value={metrics.caregiver_metrics.active_caregivers}
            icon={Users}
            color="purple"
          />
          <MetricCard
            label="With Assigned Clients"
            description="Caregivers currently serving clients"
            value={metrics.caregiver_metrics.fully_assigned}
            icon={Briefcase}
            color="teal"
          />
          <MetricCard
            label="Available for Assignment"
            description="Caregivers ready for new clients"
            value={metrics.caregiver_metrics.unassigned}
            icon={UserPlus}
            color={metrics.caregiver_metrics.unassigned > 0 ? "orange" : "gray"}
          />
          <MetricCard
            label="Near Overtime"
            description="Approaching weekly hour limits"
            value={metrics.caregiver_metrics.near_overtime}
            icon={TrendingUp}
            color={metrics.caregiver_metrics.near_overtime > 0 ? "red" : "gray"}
          />
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="tab-clients">
            <Heart className="h-4 w-4" />
            Client View
          </TabsTrigger>
          <TabsTrigger value="caregivers" className="flex items-center gap-2" data-testid="tab-caregivers">
            <Users className="h-4 w-4" />
            Caregiver View
          </TabsTrigger>
        </TabsList>

        {/* Client View */}
        <TabsContent value="clients" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Client List */}
            <div className="lg:col-span-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-10"
                  data-testid="client-search"
                />
              </div>

              <div className="space-y-4 max-h-[calc(100vh-400px)] overflow-y-auto">
                {/* Missing Primary - Urgent */}
                {filteredMissingPrimary.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-red-600 flex items-center gap-2 sticky top-0 bg-background py-1">
                      <AlertTriangle className="h-4 w-4" />
                      Missing Primary ({filteredMissingPrimary.length})
                    </h3>
                    {filteredMissingPrimary.map(client => (
                      <ClientCard
                        key={client.id}
                        client={client}
                        isSelected={selectedClient?.id === client.id}
                        onSelect={() => handleSelectClient(client)}
                        onAssign={() => handleAssignToClient(client)}
                        urgent
                      />
                    ))}
                  </div>
                )}

                {/* Missing Backup */}
                {filteredMissingBackup.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-amber-600 flex items-center gap-2 sticky top-0 bg-background py-1">
                      <AlertCircle className="h-4 w-4" />
                      Missing Backup ({filteredMissingBackup.length})
                    </h3>
                    {filteredMissingBackup.map(client => (
                      <ClientCard
                        key={client.id}
                        client={client}
                        isSelected={selectedClient?.id === client.id}
                        onSelect={() => handleSelectClient(client)}
                        onAssign={() => handleAssignToClient(client)}
                      />
                    ))}
                  </div>
                )}

                {/* Fully Assigned */}
                {filteredFullyAssigned.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-green-600 flex items-center gap-2 sticky top-0 bg-background py-1">
                      <CheckCircle className="h-4 w-4" />
                      Fully Assigned ({filteredFullyAssigned.length})
                    </h3>
                    {filteredFullyAssigned.slice(0, 15).map(client => (
                      <ClientCard
                        key={client.id}
                        client={client}
                        isSelected={selectedClient?.id === client.id}
                        onSelect={() => handleSelectClient(client)}
                        onAssign={() => handleAssignToClient(client)}
                      />
                    ))}
                    {filteredFullyAssigned.length > 15 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        +{filteredFullyAssigned.length - 15} more clients
                      </p>
                    )}
                  </div>
                )}

                {/* Empty State */}
                {!filteredMissingPrimary.length && !filteredMissingBackup.length && !filteredFullyAssigned.length && (
                  <Card className="py-8">
                    <CardContent className="text-center">
                      <Heart className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground">No clients found</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Care Team Detail */}
            <div className="lg:col-span-2">
              {selectedClient && clientCareTeam ? (
                <CareTeamDetail
                  careTeam={clientCareTeam}
                  onAssign={() => handleAssignToClient(selectedClient)}
                  onEndRelationship={handleEndRelationship}
                />
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[400px]">
                  <CardContent className="text-center">
                    <Heart className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Select a client to view their care team</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Caregiver View */}
        <TabsContent value="caregivers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Caregiver List */}
            <div className="lg:col-span-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search caregivers..."
                  value={caregiverSearch}
                  onChange={(e) => setCaregiverSearch(e.target.value)}
                  className="pl-10"
                  data-testid="caregiver-search"
                />
              </div>

              <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
                {filteredCaregivers.map(caregiver => (
                  <CaregiverCard
                    key={caregiver.id}
                    caregiver={caregiver}
                    isSelected={selectedCaregiver?.id === caregiver.id}
                    onSelect={() => handleSelectCaregiver(caregiver)}
                  />
                ))}
                {filteredCaregivers.length === 0 && (
                  <Card className="py-8">
                    <CardContent className="text-center">
                      <Users className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground">No caregivers found</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Caregiver Detail */}
            <div className="lg:col-span-2">
              {selectedCaregiver && caregiverAssignments ? (
                <CaregiverDetail
                  assignments={caregiverAssignments}
                  onEndRelationship={handleEndRelationship}
                />
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[400px]">
                  <CardContent className="text-center">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Select a caregiver to view their assignments</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Caregiver to {assignTarget?.name}</DialogTitle>
            <DialogDescription>
              Add a caregiver to this client&apos;s care team
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Caregiver Selection */}
            <div className="space-y-2">
              <Label>Select Caregiver</Label>
              <Select
                value={assignForm.caregiver_id}
                onValueChange={(v) => setAssignForm({...assignForm, caregiver_id: v})}
              >
                <SelectTrigger data-testid="select-caregiver">
                  <SelectValue placeholder="Choose a caregiver" />
                </SelectTrigger>
                <SelectContent>
                  {availableCaregivers.map(cg => (
                    <SelectItem key={cg.id} value={cg.id}>
                      <div className="flex items-center gap-2">
                        <span>{cg.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({cg.assignment_count} clients)
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assignment Tier */}
            <div className="space-y-2">
              <Label>Assignment Type</Label>
              <Select
                value={assignForm.tier}
                onValueChange={(v) => setAssignForm({...assignForm, tier: v})}
              >
                <SelectTrigger data-testid="select-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={TIER_STYLES.primary}>Primary</Badge>
                      <span className="text-xs text-muted-foreground">— Normally provides care</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="secondary">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={TIER_STYLES.secondary}>Secondary</Badge>
                      <span className="text-xs text-muted-foreground">— Preferred replacement</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="backup">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={TIER_STYLES.backup}>Backup</Badge>
                      <span className="text-xs text-muted-foreground">— Available for coverage</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any notes about this assignment..."
                value={assignForm.notes}
                onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
              />
            </div>

            {/* Validation Warnings */}
            {validationWarnings && validationWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                <h4 className="font-medium text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Validation Warnings
                </h4>
                <ul className="space-y-1">
                  {validationWarnings.map((w, i) => (
                    <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs shrink-0 ${w.level === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {w.category}
                      </span>
                      {w.message}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-600 mt-2">
                  You can override these warnings if appropriate.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            {validationWarnings && validationWarnings.length > 0 ? (
              <Button
                onClick={() => handleSubmitAssignment(true)}
                disabled={assignLoading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {assignLoading ? "Assigning..." : "Override & Assign"}
              </Button>
            ) : (
              <Button
                onClick={() => handleSubmitAssignment(false)}
                disabled={assignLoading || !assignForm.caregiver_id}
                data-testid="confirm-assign-btn"
              >
                {assignLoading ? "Assigning..." : "Assign Caregiver"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Metric Card Component with friendly description
function MetricCard({ label, description, value, icon: Icon, color, urgent }) {
  const colorClasses = {
    blue: "from-blue-50 border-blue-100 text-blue-500",
    green: "from-green-50 border-green-100 text-green-500",
    red: "from-red-50 border-red-100 text-red-500",
    amber: "from-amber-50 border-amber-100 text-amber-500",
    purple: "from-purple-50 border-purple-100 text-purple-500",
    teal: "from-teal-50 border-teal-100 text-teal-500",
    orange: "from-orange-50 border-orange-100 text-orange-500",
    gray: "from-gray-50 border-gray-100 text-gray-400",
  };

  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]} to-white group relative`}>
      <CardContent className="pt-3 pb-3 px-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <Icon className={`h-4 w-4 ${colorClasses[color].split(' ').pop()}`} />
        </div>
        <p className={`text-2xl font-bold mt-1 ${urgent ? 'text-red-600' : ''}`}>{value}</p>
        {description && (
          <p className="text-[10px] text-muted-foreground mt-1 leading-tight opacity-70">
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Client Card Component
function ClientCard({ client, isSelected, onSelect, onAssign, urgent }) {
  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-primary' : 'hover:shadow-md'
      } ${urgent ? 'border-red-200' : ''}`}
      onClick={onSelect}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white ${
              urgent ? 'bg-red-500' : 'bg-primary'
            }`}>
              {client.name?.charAt(0) || "?"}
            </div>
            <div>
              <p className="font-medium text-sm">{client.name}</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {client.has_primary && <Badge variant="outline" className="text-[10px] py-0 px-1 h-4 bg-green-50">P</Badge>}
                {client.has_secondary && <Badge variant="outline" className="text-[10px] py-0 px-1 h-4 bg-blue-50">S</Badge>}
                {client.has_backup && <Badge variant="outline" className="text-[10px] py-0 px-1 h-4 bg-gray-50">B</Badge>}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onAssign();
            }}
            data-testid={`assign-btn-${client.id}`}
          >
            <UserPlus className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Care Team Detail Component
function CareTeamDetail({ careTeam, onAssign, onEndRelationship }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">{careTeam.client_name}</CardTitle>
            <CardDescription>Care Team</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {careTeam.continuity_score !== null && (
              <Badge variant="outline" className={
                careTeam.continuity_score >= 80 ? 'bg-green-50 text-green-700' :
                careTeam.continuity_score >= 50 ? 'bg-amber-50 text-amber-700' :
                'bg-red-50 text-red-700'
              }>
                {careTeam.continuity_score}% Continuity
              </Badge>
            )}
            <Button size="sm" onClick={onAssign} data-testid="add-to-team-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add to Team
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary Caregiver */}
        <CaregiverSlot
          tier="primary"
          assignment={careTeam.primary_caregiver}
          onEnd={onEndRelationship}
        />

        {/* Secondary Caregiver */}
        <CaregiverSlot
          tier="secondary"
          assignment={careTeam.secondary_caregiver}
          onEnd={onEndRelationship}
        />

        {/* Backup Caregivers */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Badge className={TIER_STYLES.backup}>Backup</Badge>
              <span className="text-muted-foreground font-normal">
                ({careTeam.backup_caregivers?.length || 0})
              </span>
            </h4>
          </div>
          {careTeam.backup_caregivers?.length > 0 ? (
            <div className="space-y-2">
              {careTeam.backup_caregivers.map(cg => (
                <CaregiverAssignmentCard
                  key={cg.id}
                  assignment={cg}
                  onEnd={onEndRelationship}
                  compact
                />
              ))}
            </div>
          ) : (
            <Card className="border-dashed bg-muted/50">
              <CardContent className="p-3 text-center text-muted-foreground text-sm">
                No backup caregivers assigned
              </CardContent>
            </Card>
          )}
        </div>

        {/* Assignment History */}
        {careTeam.assignment_history?.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground">Assignment History</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {careTeam.assignment_history.slice(0, 5).map(h => (
                <div key={h.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                  <span>{h.caregiver_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {h.effective_date} - {h.end_date || "Present"} · {h.end_reason}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Caregiver Slot Component (for Primary/Secondary)
function CaregiverSlot({ tier, assignment, onEnd }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">
        <Badge className={TIER_STYLES[tier]}>{TIER_LABELS[tier]}</Badge>
      </h4>
      {assignment ? (
        <CaregiverAssignmentCard assignment={assignment} onEnd={onEnd} />
      ) : (
        <Card className="border-dashed bg-muted/50">
          <CardContent className="p-3 text-center text-muted-foreground text-sm">
            No {tier} caregiver assigned
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Caregiver Assignment Card
function CaregiverAssignmentCard({ assignment, onEnd, compact }) {
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [endReason, setEndReason] = useState("");

  return (
    <>
      <Card>
        <CardContent className={compact ? "p-3" : "p-4"}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`${compact ? 'h-8 w-8' : 'h-10 w-10'} rounded-full bg-primary/10 flex items-center justify-center`}>
                <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-primary`}>
                  {assignment.caregiver_name?.split(" ").map(n => n[0]).join("") || "?"}
                </span>
              </div>
              <div>
                <p className={`font-medium ${compact ? 'text-sm' : ''}`}>{assignment.caregiver_name}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {assignment.last_visit && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Last: {new Date(assignment.last_visit).toLocaleDateString()}
                    </span>
                  )}
                  {assignment.next_scheduled_visit && (
                    <span className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      Next: {new Date(assignment.next_scheduled_visit).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-red-600"
              onClick={() => setShowEndDialog(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* End Relationship Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End Assignment</DialogTitle>
            <DialogDescription>
              Remove {assignment.caregiver_name} from this care team?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Reason</Label>
            <Textarea
              placeholder="Why is this assignment ending?"
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                onEnd(assignment.id, endReason);
                setShowEndDialog(false);
              }}
            >
              End Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Caregiver Card Component
function CaregiverCard({ caregiver, isSelected, onSelect }) {
  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-primary' : 'hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
              {caregiver.name?.split(" ").map(n => n[0]).join("") || "?"}
            </div>
            <div>
              <p className="font-medium text-sm">{caregiver.name}</p>
              <p className="text-xs text-muted-foreground">
                {caregiver.assignment_count} clients assigned
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

// Caregiver Detail Component
function CaregiverDetail({ assignments, onEndRelationship }) {
  const workload = assignments.workload || {};
  const compliance = assignments.compliance || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">{assignments.caregiver_name}</CardTitle>
            <CardDescription>
              {assignments.primary_count} Primary · {assignments.secondary_count} Secondary · {assignments.backup_count} Backup
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Compliance Badge */}
            <Badge variant="outline" className={
              compliance.status === "compliant" ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }>
              <Shield className="h-3 w-3 mr-1" />
              {compliance.status === "compliant" ? "Compliant" : "Issues"}
            </Badge>
            
            {/* Overtime Risk */}
            {workload.overtime_risk && workload.overtime_risk !== "none" && (
              <Badge variant="outline" className={
                workload.overtime_risk === "high" ? 'bg-red-50 text-red-700' :
                workload.overtime_risk === "medium" ? 'bg-amber-50 text-amber-700' :
                'bg-yellow-50 text-yellow-700'
              }>
                <TrendingUp className="h-3 w-3 mr-1" />
                OT Risk
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Workload Stats */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="text-center">
            <p className="text-2xl font-bold">{workload.weekly_scheduled_hours || 0}</p>
            <p className="text-xs text-muted-foreground">Hours This Week</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{workload.max_weekly_hours || 40}</p>
            <p className="text-xs text-muted-foreground">Max Hours</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{workload.utilization_percent || 0}%</p>
            <p className="text-xs text-muted-foreground">Utilization</p>
          </div>
        </div>

        {/* Compliance Issues */}
        {compliance.issues?.length > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="text-sm font-medium text-red-800 mb-2">Compliance Issues</h4>
            <ul className="space-y-1">
              {compliance.issues.map((issue, i) => (
                <li key={i} className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3" />
                  {issue}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Assigned Clients */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Assigned Clients</h4>
          {assignments.assigned_clients?.length > 0 ? (
            <div className="space-y-2">
              {assignments.assigned_clients.map(client => (
                <Card key={client.id} className="bg-muted/30">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={TIER_STYLES[client.tier]}>
                          {TIER_LABELS[client.tier]}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{client.client_name}</p>
                          {client.client_address && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {client.client_address}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        {client.next_scheduled_visit && (
                          <p>Next: {new Date(client.next_scheduled_visit).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed bg-muted/50">
              <CardContent className="p-4 text-center text-muted-foreground text-sm">
                No clients assigned
              </CardContent>
            </Card>
          )}
        </div>

        {/* Skills & Languages */}
        {(assignments.skills?.length > 0 || assignments.languages?.length > 0) && (
          <div className="grid grid-cols-2 gap-4 pt-2">
            {assignments.skills?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Skills</h4>
                <div className="flex flex-wrap gap-1">
                  {assignments.skills.map(skill => (
                    <Badge key={skill} variant="outline" className="text-xs">{skill}</Badge>
                  ))}
                </div>
              </div>
            )}
            {assignments.languages?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Languages</h4>
                <div className="flex flex-wrap gap-1">
                  {assignments.languages.map(lang => (
                    <Badge key={lang} variant="outline" className="text-xs">{lang}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
